// Voroforce config for the Explore page.
// Adapted from gnovotny/nothing-to-watch (app/vf/config), with the mode/theme/preset
// machinery stripped: uniforms are flattened to their "preview mode, dark theme" values.
import mainFrag from "./main.frag";

export const CELLS = Number(process.env.REACT_APP_VORO_CELLS) || 2160;
export const MEDIA_BASE_URL = process.env.REACT_APP_VORO_MEDIA_URL || "/voro/media";

// Grid geometry copied verbatim from upstream so the shader/lattice math stays known-good.
// Tile sizes: v0 4x6, v1 22x33, v2 110x165. Layers get clamped to the cell count by the engine.
export const MEDIA_VERSIONS = [
  { cols: 512, rows: 104, width: 2048, height: 624, layers: 1, layerSrcFormat: "/low/{EXT}/{INDEX}.{EXT}" },
  { cols: 90, rows: 60, width: 1980, height: 1980, layers: 10, layerSrcFormat: "/mid/{EXT}/{INDEX}.{EXT}" },
  { cols: 18, rows: 12, width: 1980, height: 1980, layers: 241, layerSrcFormat: "/high/{EXT}/{INDEX}.{EXT}" },
];

// The smallest layer capacity (v2: 18*12) is the "subgrid" size, which is also the
// film-metadata batch size: cell.subgrid -> {n}.json, cell.subgridIndex -> item in it.
export const SUBGRID_SIZE = 18 * 12;

const config = {
  cells: CELLS,
  // ponytail: single-threaded. Multi-threading needs SharedArrayBuffer (COOP/COEP headers)
  // and webpack worker resolution; turn it on only if 2k+ cells actually stutter.
  multiThreading: { enabled: false, renderInParallel: false },
  devTools: { enabled: false },
  handleVisibilityChange: { enabled: true, hiddenDelay: 5000 },
  lattice: { enabled: true, aspect: 2 / 3 },
  media: {
    enabled: true,
    baseUrl: MEDIA_BASE_URL,
    // 'v0' only: mid/high layers load on demand, and with a random slice the first
    // layer of each version usually isn't the one we need anyway.
    preload: "v0",
    compressionFormat: "dds",
    versions: MEDIA_VERSIONS,
  },
  controls: {
    debug: false,
    autoFocusCenter: { enabled: true, random: true },
    maxSpeed: 10,
    ease: 0.15,
    easePinned: 0.15,
    // Upstream's "detach gesture": a fast flick freezes the pointer until you move back to
    // the frozen cell. Without upstream's side panel it just reads as a stuck cursor.
    freezeOnShake: { enabled: false },
    freezeOnJolt: { enabled: false },
    zoom: { enabled: true, min: 1, max: 1.5 },
  },
  simulation: {
    steps: {
      force: {
        parameters: {
          alpha: 0.15,
          velocityDecay: 0.7,
          velocityDecayBase: 0.7,
          velocityDecayTransitionEnterMode: 0.9,
        },
        forces: {
          manageWeights: true,
          primaryCellWeightPushFactorEnabled: true,
          smoothPrimaryCell: true,
          requestMediaVersions: { enabled: true },
          breathing: { enabled: true },
          push: {
            strength: 0.1,
            yFactor: 2.5,
            alignmentMaxLevelsX: 40,
            centerXStretchMod: 0.4,
            speedFactor: 1,
          },
          lattice: { strength: 0.8, yFactor: 3.75, xFactor: 1, maxLevelsFromPrimary: 30 },
          origin: { strength: 0.8, yFactor: 1.5 },
        },
      },
      neighbors: { levels: 1 },
    },
  },
  display: {
    scene: {
      dev: { enabled: false },
      main: {
        fragmentShader: mainFrag,
        uniforms: {
          iForcedMaxNeighborLevel: { value: 0 },
          fPixelSearchRadiusMod: { value: 1 },
          bMediaDistortion: { value: false },
          fMediaBboxScale: { value: 1 },
          fBaseColor: { value: [0, 0, 0] },
          fBorderRoundnessMod: { value: 0.75 },
          fBorderThicknessMod: { value: 1 },
          fBorderSmoothnessMod: { value: 1 },
          fCenterForceBulgeStrength: { value: 0.75 },
          fCenterForceBulgeRadius: { value: 0.75 },
          fWeightOffsetScaleMod: { value: 0.25 },
          fWeightOffsetScaleMediaMod: { value: 1 },
          fUnweightedEffectMod: { value: 1 },
          fBaseXDistScale: { value: 1.5 },
          fWeightedXDistScale: { value: 1.5 },
          fRippleMod: { value: 1 },
          fNoiseOctaveMod: { value: 1 },
          fNoiseCenterOffsetMod: { value: 1 },
        },
      },
      post: { enabled: false, fragmentShader: undefined, uniforms: {} },
    },
  },
};

export default config;
